import {useState, useMemo, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CurrencyValue } from '../../../common/currencyValue';
import Checkbox from '../../../common/simple/Checkbox';
import calendar from '../../../images/newDesign/calendarWithCheck.svg';
import Button from '../../../common/Button';
import arrow from '../../../images/newDesign/arrow_black.svg'; 
import { StyledMonthlyCard } from './MonthlyCard.styled';
import MonthlyItem from './MonthlyItem';
import {
    selectDebtPayment,
    selectOngoingPayment,
    setDebtSelectedPayments,
    setOnGoingSelectedPayments
} from "../../../store/tenant/slice";
import { paymentsCardType } from './PaymentPageLogic';

export function MonthlyCard({ onMarkAsPaid = () => void undefined }) {
    const { t } = useTranslation();

    const ltr = useSelector(state => state.common.ltrEnabled);
    const dispatch = useDispatch();

    const ref = useRef(null);
    const btnRef = useRef(null);

    const allPayments = useSelector(state => state.tenant.allPayments);
    const onGoingSelected = useSelector(state => state.tenant.onGoingSelectedPayments);
    const pastSelected = useSelector(state => state.tenant.debtSelectedPayments);
    const buildingMarkAsPaidDisabled = useSelector(state => state.tenant.buildingMarkAsPaidDisabled);

    const [infoOpened, setInfoOpened] = useState(false);

    const onGoingPayments = useMemo(() => allPayments?.on_going_payments?.paymentsSortedArray ?? [], [allPayments]);
    const pastPayments = useMemo(() => allPayments?.past_payments?.paymentsSortedArray ?? [], [allPayments]);

    const onGoingPaymentItems = useMemo(() => {
        return onGoingPayments.map(payment => ({ payment, isChecked: onGoingSelected.some(p => p.uid === payment.uid) }));
    }, [onGoingPayments, onGoingSelected]);

    const pastPaymentItems = useMemo(() => {
        return pastPayments.map(payment => ({ payment, isChecked: pastSelected.some(p => p.uid === payment.uid) }));
    }, [pastPayments, pastSelected]);

    const allOnGoingChecked = useMemo(() => {
        return onGoingPayments.length === onGoingSelected.length && onGoingSelected.length > 0;
    }, [onGoingPayments, onGoingSelected])

    const allPastChecked = useMemo(() => {
        return pastPayments.length === pastSelected.length && pastSelected.length > 0;
    }, [pastPayments, pastSelected]);

    const selectedMonths = useMemo(() => onGoingSelected.length + pastSelected.length, [onGoingSelected, pastSelected]);

    const monthlyFee = useMemo(() => {
        return onGoingPayments[0]?.total?.value ?? 0;
    }, [onGoingPayments]);

    const debtAmount = useMemo(() => {
        return pastPayments.reduce((acc, p) => acc + p.amount_left?.value, 0);
    }, [onGoingPayments, pastPayments]);

    const currency = useMemo(() => {
        return onGoingPayments[0]?.amount_left?.currency ?? pastPayments[0]?.amount_left?.currency;
    }, [onGoingPayments, pastPayments]);

    // logic for checkboxes for ongoing and past payments divided, this foo is for ongoing checkboxes
    // if a payment is selected, payments for all previous months are automatically selected too
    function handleOnGoingCheckboxChange(paymentItem) {
        dispatch(selectOngoingPayment(paymentItem));
    }

    // past payments checkboxes
    function handlePastCheckboxChange(paymentItem) {
        dispatch(selectDebtPayment(paymentItem));
    }

    function handleAllOnGoingCheckboxChange(checked) {
        dispatch(setOnGoingSelectedPayments(checked ? onGoingPayments : []));
    }

    function handleAllPastCheckboxChange(checked) {
        dispatch(setDebtSelectedPayments(checked ? pastPayments : []));
    }

    function MonthlyPaymentList({paymentItems, onCheckboxChange}) {
        return paymentItems.map((paymentItem, i) => (
            <MonthlyItem
                key={i}
                paymentItem={paymentItem}
                onChange={onCheckboxChange}
                currency={currency}
            />
        ));
    }

    const handleClickOutsideDropdown = e => {
        if (ref.current && !ref.current.contains(e.target)) setInfoOpened(false);
        else if (btnRef.current && btnRef.current.contains(e.target)) setInfoOpened(true);
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutsideDropdown, true);
    }, []);

    const closeDropdown = () => setInfoOpened(false);

    return (
        <StyledMonthlyCard opened={infoOpened} ltr={ltr}>
            <div className='card-title border-bottom'>
                <img src={calendar} />
                {t('reports.monthlyFee')}
            </div>

            <div className='payment border-bottom'>
                {t('payments.debt_amount')}
                <CurrencyValue notInCents={true} currency={currency}>{debtAmount}</CurrencyValue>                          
            </div>

            <div className='payment border-bottom'>
                {t('payments.monthly_fee')}
                <CurrencyValue notInCents={true} currency={currency}>{monthlyFee}</CurrencyValue>                          
            </div>

            <div className='payment'>
                {t('payments.selected_months')}
                <div className='value'>{selectedMonths}</div>
            </div>

            <div className='dropdown' ref={btnRef}>
                {t('payments.months_details')}
                <img src={arrow} className='dropdown-arrow' />
            </div>

            {infoOpened && (
                <div className='info' ref={ref}>
                    {pastPaymentItems.length > 0 && (
                        <>                       
                            <div className='subheader'>
                                <Checkbox
                                    checked={allPastChecked}
                                    onChange={handleAllPastCheckboxChange}
                                    wrapperClassName='check-all'
                                />
                                <div>{t('payments.past')}</div>
                            </div>
                            <MonthlyPaymentList paymentItems={pastPaymentItems} onCheckboxChange={handlePastCheckboxChange} />
                        </>
                    )}

                    {onGoingPaymentItems.length > 0 && (
                        <>                       
                            <div className='subheader'>
                                <Checkbox
                                    checked={allOnGoingChecked}
                                    onChange={handleAllOnGoingCheckboxChange}
                                    wrapperClassName='check-all'
                                />
                                <div>{t('payments.on_going')}</div>
                            </div>
                            <MonthlyPaymentList paymentItems={onGoingPaymentItems} onCheckboxChange={handleOnGoingCheckboxChange} />
                        </>
                    )}

                    {!buildingMarkAsPaidDisabled && (
                        <div onClick={onMarkAsPaid} className='mark-as-paid'>{t('payments.mark_as_paid')}</div>
                    )}

                    <Button outlined onClick={closeDropdown}>{t('payments.apply')}</Button>
                </div>
            )}
        </StyledMonthlyCard>
    )
}
